import { Controller } from '@hotwired/stimulus'

/*
 * This is controller is for search forms
 * It can auto-submit the form after a brief timeout
 * on any input that is marked as an input target.
 * It will update the url's query params with the input params.
 *
 * You can also connect other stimulus controllers by having an element's 'data-action'
 * attribute listen to custom events "search@window" or "#{param}-param-update@window"
 *
 * You need an endpoint that accepts POST requests
 */
export default class extends Controller {
  static targets = ['input', 'spinner', 'form', 'content']
  static values = {
    minQueryLength: Number, // minimum query length; defaults to 0
    queryParam: { type: String, default: 'search' }, // the name of the url parameter that holds the query
    submitOnLoad: { type: Boolean, default: true }, // whether or not the search should submit on page load (useful when results are rendered with Turbo)
  }

  connect() {
    // Since auto search is in place, we do not want users to be able to
    // submit the search form by pressing enter
    this.inputTargets.forEach((element) => {
      if (element.tagName.toLowerCase() !== 'input' || element.type !== 'text')
        return

      element.addEventListener('keypress', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault()
        }
      })

      // Submit search form when page is initially loaded
      if (this.submitOnLoadValue) {
        element.dispatchEvent(new Event('input'))
      }
    })
  }

  autoSubmit() {
    this.updateQueryParams()

    const url = new URL(window.location.href)
    const form = this.targets.find('form') || this.element

    clearTimeout(this.timeout)

    // Return early if the current search query does not meet the minimum length requirement
    if (
      this.minQueryLengthValue >
      (url.searchParams.get(this.queryParamValue)?.length || 0)
    )
      return

    this.timeout = setTimeout(() => {
      if (this.hasSpinnerTarget) {
        this.spinnerTargets.forEach((spinner) =>
          spinner.classList.remove('hidden'),
        )
        this.contentTargets.forEach((element) =>
          element.classList.add('hidden'),
        )
      }

      form.requestSubmit()
      const search = new CustomEvent('search')
      window.dispatchEvent(search)

      if (this.hasSpinnerTarget) {
        form.addEventListener('turbo:submit-end', () => {
          if (this.hasSpinnerTarget) {
            this.spinnerTargets.forEach((spinner) =>
              spinner.classList.add('hidden'),
            )
            this.contentTargets.forEach((element) =>
              element.classList.remove('hidden'),
            )
          }
        })
      }
    }, 500)
  }

  // On form submit, update the url's query params
  updateQueryParams() {
    const previousUrl = new URL(window.location.href)

    // Start with existing URL parameters to preserve non-form parameters
    const urlParams = new URLSearchParams(previousUrl.search)

    const state = {}
    this.inputTargets.forEach((input) => {
      const value = input.type === 'checkbox' ? input.checked : input.value
      if (value) {
        state[input.id] = value
        urlParams.set(input.id, value)
      } else {
        // Remove empty parameters
        urlParams.delete(input.id)
      }
    })

    const url = new URL(
      `${previousUrl.origin}${previousUrl.pathname}?${urlParams.toString()}`,
    )
    window.history.replaceState(state, null, url.href)

    // Fire an event for each changed parameter
    // The event can be listened to by including "<parameter>-param-update@window-><action>"
    // in a data-action attribute
    this.inputTargets.forEach((input) => {
      if (
        url.searchParams.get(input.id) ===
        previousUrl.searchParams.get(input.id)
      )
        return

      const paramupdate = new CustomEvent(`${input.id}-param-update`, {
        detail: {
          value: url.searchParams.get(input.id) || '',
        },
      })
      window.dispatchEvent(paramupdate)
    })
  }
}
